import {
  apiService,
  coreLessonPlanDB,
  HasUsedExamChecker,
  lessonPlanDB,
  notificationDb,
  posthog,
  posthogIdentify,
  resetDB,
  studentDB,
  syncedDBModel,
  syncStateManager,
  TBConfig,
  teacherTrainingDB,
  userDB,
} from '..';
import { examCheckerWalkThroughPhase } from '../../../../apps/school-app/src/features/exam-checker/constants';

/**
 * id is the user id.
 * recordId is the id of the record in the local db.
 */

export const configKey = 'config';

const config: TBConfig = {
  appLogoPath: 'niete-logo.svg',
  appLogoWidthNavbar: '240',
  appLogoWidthLanding: '550',
  app: 'niete',
};

interface UserValidationResponse {
  hasProfiles: boolean;
  hasParent: boolean;
  hasTeacher: boolean;
  hasCoach: boolean;
  isPasswordSet: boolean;
}

export interface ValidationResult {
  route: string | undefined;
  hasProfile: boolean;
}

const validateUserProfile = (
  data: UserValidationResponse,
): ValidationResult => {
  const hasProfile = data.hasProfiles;

  let route = undefined;

  if (hasProfile) {
    route = data.isPasswordSet ? '/set-password' : '/create-password';
  }

  return { route, hasProfile };
};

export const validateUserEndpoint = async (phoneNumber: string) => {
  const body = {
    username: phoneNumber,
  };
  const response = await apiService.post('/api/v1/users/state/', body);
  return validateUserProfile(response.data);
};

export const verifyUser = async (phoneNumber: string) => {
  const body = {
    username: phoneNumber,
  };
  const response = await apiService.post('/api/v1/users/state/', body);
  return response.data;
};

export const loginUser = async (phoneNumber: string, password: string) => {
  const body = {
    username: phoneNumber,
    password: password,
  };

  const response = await apiService.post('/users/login/', body);
  await userDB.setToken(response.data);
  syncStateManager.checkChanges();
  posthogIdentify();
  return response.data;
};

/**
 * Do not call this in the view, it is called in background sync.
 */
export const userDetails = async () => {
  const response = await apiService.get(`api/v1/users/`);
  const userProfile = response.data[0];

  // Always update the database with the response, even if no profiles
  // This ensures useLiveQuery components can react to the change
  if (userProfile) {
    console.log(
      '[USERDETAILS] Backend returned profiles:',
      userProfile.profiles?.map((p: any) => ({
        id: p.id,
        levels: p.levels,
        isSelected: p.isSelected,
      })),
    );
    userDB.setUserProfile(userProfile);
  }

  return userProfile;
};

export const createPassword = async (
  phoneNumber: string,
  password: string,
  confirmPassword: string,
) => {
  const body = {
    username: phoneNumber,
    password: password,
    confirmPassword: confirmPassword,
  };
  const response = await apiService.post('users/create-password/', body);
  // wait here until its set or next call will fail.
  await userDB.setToken(response.data);
  syncStateManager.checkChanges();
  return response.data;
};
export const updatePassword = async (
  phoneNumber: string,
  password: string,
  confirmPassword: string,
) => {
  const body = {
    username: phoneNumber,
    password: password,
    confirmPassword: confirmPassword,
  };
  const response = await apiService.post('users/create-password/', body);
  return response.data;
};

export const sendOtp = async (phone: string) => {
  const body = { username: phone, delivery_method: 'SMS' };
  const response = await apiService.post('users/send-otp/', body);
  return response.data;
};
export const verifyOtp = async (phone: string, otp: string) => {
  const body = { username: phone, otp: otp };
  const response = await apiService.post('users/otp-verification/', body);
  return response.data;
};
export const logout = async () => {
  // TODO handle this better so that
  // we don't have to clear all the data
  // esp if its just a token expiry.
  userDB.resetToken();
  await resetDB();
  //localStorage.clear(); // todo when logging out, its also clear config data and posthog identify call is dependent on it if relogin without refresh, config data is only set on initial page load
  const keyToKeep = [
    'config',
    'ftue-timetable-new-user',
    'ftue-timetable',
    'selectedRegion',
    HasUsedExamChecker,
    examCheckerWalkThroughPhase,
    'signupCompleted',
    'hasVisitedWelcome',
  ];
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)!;
    if (!keyToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  }
  posthog?.reset(true);
};

export const resetTrainingAndLessonPlan = () => {
  teacherTrainingDB.clearData();
  lessonPlanDB.clearData();
  coreLessonPlanDB.clearData();
  syncedDBModel.resetSyncRecord();
  studentDB.clearData();
  notificationDb.clearData();
};

export const getWhiteLabelConfig = async () => {
  const response = await apiService.get('api/v1/organization/1/configuration/');
  return response.data;
};

export const setConfig = (config: TBConfig) => {
  localStorage.setItem(configKey, JSON.stringify(config));
};
export const getConfig = () => {
  const configStored = JSON.parse(
    localStorage.getItem(configKey) || 'null',
  ) as TBConfig;
  return configStored;
};

export const deactivateUser = async () => {
  const response = await apiService.post('users/deactivate/');
  return response.data;
};

export const getPasswordlessToken = async (phoneNumber: string) => {
  const body = {
    username: phoneNumber,
  };

  const response = await apiService.post('/users/tokens/admin/', body, {
    headers: {
      APIKey: process.env.NX_PUBLIC_LIVE_UPDATE_API_KEY,
    },
  });

  await userDB.setToken(response.data);
  syncStateManager.checkChanges();
  return response.data;
};
